import React from 'react';
import { Typography } from 'antd';

const { Title, Paragraph } = Typography;

export const PrivacyContentEN: React.FC = () => {
  return (
    <div className="terms-content">
      <Paragraph className="terms-intro">
        This Privacy Policy (the "Policy") describes how DivvyStash collects, uses, discloses, and safeguards 
        information about users of the DivvyStash platform and related services (collectively, the "Service").
      </Paragraph>

      <Paragraph className="terms-intro">
        By accessing or using the Service, you agree to the practices described in this Policy. If you do not 
        agree with this Policy, you must not use the Service.
      </Paragraph>

      <Title level={2} className="section-title">1. Scope</Title>
      <Paragraph>This Policy applies to information we collect:</Paragraph>
      <ul className="terms-list">
        <li>When you access or use the DivvyStash web application or any other interface we provide;</li>
        <li>When you communicate with us (such as by email or support channels);</li>
        <li>When an Admin submits information about you in order to invite you to the Service.</li>
      </ul>
      <Paragraph>
        This Policy does not apply to any third-party websites, services, or applications, even if accessed 
        through the Service.
      </Paragraph>

      <Title level={2} className="section-title">2. Information We Collect</Title>
      <Paragraph>
        We collect information in three primary ways: (a) information you provide to us, (b) information we 
        collect automatically when you use the Service, and (c) information we receive from Admins or other 
        third parties.
      </Paragraph>

      <Title level={3} className="subsection-title">2.1 Information You Provide</Title>
      <Paragraph>We may collect the following categories of information that you provide directly to us:</Paragraph>
      
      <Paragraph><strong>Account and Profile Information.</strong> Your name, email address, password (stored in 
      hashed form), country of residence, preferred language or locale, and any additional profile details you 
      choose to provide.</Paragraph>

      <Paragraph><strong>Contribution and Participation Information.</strong> When you participate as a Contributor, 
      we collect and maintain records related to your participation, such as:</Paragraph>
      <ul className="terms-list">
        <li>Minimum required contribution as set by the Admin;</li>
        <li>Confirmed contribution amounts and base principal;</li>
        <li>Reinvestment percentage preferences;</li>
        <li>Profit allocations and settlement records associated with your account;</li>
        <li>Withdrawal requests, including amounts and status (requested, approved, rejected, completed, or cancelled).</li>
      </ul>

      <Paragraph><strong>Admin Information.</strong> If you are an Admin, we collect information about your Admin 
      role, including information about Users you invite, minimum contribution settings, and profit entries you create.</Paragraph>

      <Paragraph><strong>Communications and Support.</strong> If you contact us (for example, by email or through a 
      support form), we collect the content of your message and any contact details you provide.</Paragraph>

      <Title level={3} className="subsection-title">2.2 Information We Collect Automatically</Title>
      <Paragraph>
        When you access or use the Service, we may automatically collect certain information about your device 
        and usage, including:
      </Paragraph>

      <Paragraph><strong>Device and Log Information.</strong> IP address, browser type, operating system, device 
      identifiers, pages viewed, access dates and times, referring/exit pages, and other standard log data.</Paragraph>

      <Paragraph><strong>Usage Information.</strong> Actions you take within the Service, such as viewing dashboards, 
      updating your reinvestment percentage, submitting withdrawal requests, or changing profile settings.</Paragraph>

      <Paragraph><strong>Language and Locale Information.</strong> Your browser language settings and in-app 
      language/locale preferences, which help us present content in the appropriate language.</Paragraph>

      <Paragraph>
        We may use cookies, local storage, and similar technologies to collect and store some of this information. 
        See Section 6 (Cookies and Similar Technologies) below for more details.
      </Paragraph>

      <Title level={3} className="subsection-title">2.3 Information from Admins and Third Parties</Title>
      <Paragraph>We may receive information about you from:</Paragraph>

      <Paragraph><strong>Admins.</strong> Admins may provide your name, email address, country, and minimum 
      contribution requirement in order to invite you to the Service and administer your participation.</Paragraph>

      <Paragraph><strong>Service Providers.</strong> We may receive limited information from third-party vendors 
      that provide infrastructure, analytics, email delivery, or other services (for example, email delivery status, 
      bounce information, or aggregate statistics).</Paragraph>

      <Paragraph><strong>Other Sources.</strong> Where permitted by law, we may receive information from public 
      sources or from third parties that assist with fraud prevention, security, or compliance.</Paragraph>

      <Title level={2} className="section-title">3. How We Use Information</Title>
      <Paragraph>We use the information we collect for the following purposes:</Paragraph>

      <Title level={3} className="subsection-title">3.1 To Provide and Maintain the Service</Title>
      <ul className="terms-list">
        <li>Creating and managing user accounts and profiles;</li>
        <li>Enabling Admins to invite Users and configure contribution settings;</li>
        <li>Recording and displaying contributions, base principal, profit allocations, reinvestment percentages, 
        weekly settlements, and withdrawable balances;</li>
        <li>Processing and tracking withdrawal requests and their status;</li>
        <li>Providing user dashboards and admin overviews, including performance summaries (week-to-date, 
        month-to-date, year-to-date, and all-time gains).</li>
      </ul>

      <Title level={3} className="subsection-title">3.2 To Communicate with You</Title>
      <ul className="terms-list">
        <li>Sending transactional emails, such as invites, password reset emails, contribution confirmations, 
        withdrawal request notifications, and weekly settlement summaries;</li>
        <li>Responding to your questions, comments, or support requests;</li>
        <li>Sending administrative or service-related notices, such as changes to this Policy or our Terms.</li>
      </ul>

      <Title level={3} className="subsection-title">3.3 To Personalize the Experience</Title>
      <ul className="terms-list">
        <li>Presenting the Service in your preferred language or locale;</li>
        <li>Customizing certain content based on your role (Admin vs Contributor) and account configuration.</li>
      </ul>

      <Title level={3} className="subsection-title">3.4 For Analytics and Service Improvement</Title>
      <ul className="terms-list">
        <li>Analyzing usage trends and patterns to improve the Service's functionality, performance, and user experience;</li>
        <li>Developing new features and offerings.</li>
      </ul>

      <Title level={3} className="subsection-title">3.5 For Security, Fraud Prevention, and Compliance</Title>
      <ul className="terms-list">
        <li>Protecting the security and integrity of the Service;</li>
        <li>Detecting, investigating, and preventing fraudulent or illegal activity, misuse of the Service, or 
        violations of our Terms;</li>
        <li>Complying with legal obligations, regulatory requirements, and law enforcement requests, where applicable.</li>
      </ul>

      <Title level={3} className="subsection-title">3.6 With Your Consent</Title>
      <Paragraph>
        For any other purpose disclosed at the time of collection, where we request and you provide your consent.
      </Paragraph>

      <Title level={2} className="section-title">4. Legal Bases for Processing (EEA/UK Users)</Title>
      <Paragraph>
        If you are located in the European Economic Area (EEA), the United Kingdom, or another jurisdiction that 
        requires a legal basis for processing personal data, our legal bases include:
      </Paragraph>

      <Paragraph><strong>Contract.</strong> Processing that is necessary to provide the Service and fulfill our 
      contractual obligations to you (for example, creating and maintaining your account, recording your participation, 
      and providing dashboards and summaries).</Paragraph>

      <Paragraph><strong>Legitimate Interests.</strong> Processing for our legitimate interests, including to secure 
      and improve the Service, to prevent fraud, to understand how the Service is used, and to support Admins in 
      administering participation, provided such interests are not overridden by your rights and interests.</Paragraph>

      <Paragraph><strong>Legal Obligation.</strong> Processing necessary to comply with legal obligations, such as 
      responding to lawful requests from authorities or complying with applicable recordkeeping requirements.</Paragraph>

      <Paragraph><strong>Consent.</strong> Where required by law (for example, for certain cookies or marketing 
      communications), we may rely on your consent. You have the right to withdraw consent at any time, without 
      affecting the lawfulness of processing based on consent before its withdrawal.</Paragraph>

      <Title level={2} className="section-title">5. How We Share Information</Title>
      <Paragraph>We do not sell your personal information. We may share information as follows:</Paragraph>

      <Title level={3} className="subsection-title">5.1 With Admins and Other Users (as Appropriate)</Title>
      <ul className="terms-list">
        <li>Admins may view information necessary to administer contributions, profits, and withdrawals for Users 
        they manage.</li>
        <li>Users may see certain aggregated or derived information, such as their own share of the pool and overall 
        pool totals. The design of the Service does not require one User to see other Users' personal details, but 
        some aggregated metrics may reflect group-level performance.</li>
      </ul>

      <Title level={3} className="subsection-title">5.2 With Service Providers</Title>
      <Paragraph>
        We may share information with third-party vendors and service providers that perform services on our behalf, 
        such as hosting, infrastructure, email delivery, logging, analytics, and customer support. These providers 
        are authorized to use your information only as necessary to provide services to us.
      </Paragraph>

      <Title level={3} className="subsection-title">5.3 For Legal and Compliance Purposes</Title>
      <Paragraph>We may disclose information if we believe in good faith that such disclosure is reasonably necessary to:</Paragraph>
      <ul className="terms-list">
        <li>Comply with any applicable law, regulation, legal process, or governmental request;</li>
        <li>Enforce our Terms or other agreements;</li>
        <li>Protect the rights, property, or safety of DivvyStash, our users, or others;</li>
        <li>Detect, prevent, or otherwise address fraud, security, or technical issues.</li>
      </ul>

      <Title level={3} className="subsection-title">5.4 Business Transfers</Title>
      <Paragraph>
        In connection with, or during negotiations of, any merger, sale of company assets, financing, acquisition, 
        or similar transaction, we may transfer information to another company, subject to appropriate confidentiality 
        protections.
      </Paragraph>

      <Title level={3} className="subsection-title">5.5 With Your Consent</Title>
      <Paragraph>We may share information with your consent or at your direction.</Paragraph>

      <Title level={2} className="section-title">6. Cookies and Similar Technologies</Title>
      <Paragraph>We may use cookies, local storage, and similar technologies to:</Paragraph>
      <ul className="terms-list">
        <li>Keep you signed in;</li>
        <li>Remember your preferences (such as language);</li>
        <li>Analyze usage of the Service.</li>
      </ul>
      <Paragraph>
        You can typically configure your browser to refuse cookies or to indicate when a cookie is being sent. 
        However, some parts of the Service may not function properly if cookies are disabled.
      </Paragraph>
      <Paragraph>
        Where required by law, we will obtain your consent before setting non-essential cookies.
      </Paragraph>

      <Title level={2} className="section-title">7. Data Retention</Title>
      <Paragraph>We retain personal information for as long as reasonably necessary to:</Paragraph>
      <ul className="terms-list">
        <li>Provide the Service and maintain your account;</li>
        <li>Fulfill the purposes described in this Policy;</li>
        <li>Comply with legal, tax, or accounting obligations;</li>
        <li>Resolve disputes and enforce our agreements.</li>
      </ul>
      <Paragraph>
        The specific retention period may vary depending on the type of data and the purposes for which we process it. 
        When information is no longer needed for these purposes, we will delete it or anonymize it, subject to any 
        technical or legal limitations.
      </Paragraph>

      <Title level={2} className="section-title">8. Data Security</Title>
      <Paragraph>
        We use reasonable technical and organizational measures designed to protect the information we collect and 
        store. However, no method of transmission over the internet or method of electronic storage is completely secure. 
        Accordingly, we cannot guarantee absolute security.
      </Paragraph>
      <Paragraph>
        You are responsible for choosing a strong password and keeping your credentials confidential. If you believe 
        your account has been compromised, you should contact us immediately.
      </Paragraph>

      <Title level={2} className="section-title">9. International Transfers</Title>
      <Paragraph>
        DivvyStash is based in the United States, and the information we collect may be transferred to, stored in, 
        and processed in that country and in other countries where our service providers are located. These countries 
        may have data protection laws that are different from those in your country of residence.
      </Paragraph>
      <Paragraph>
        Where required by law, we will implement appropriate safeguards to protect personal data transferred across 
        borders, such as standard contractual clauses or other lawful transfer mechanisms.
      </Paragraph>

      <Title level={2} className="section-title">10. Data Retention</Title>
      <Paragraph>
        We retain your personal information for as long as necessary to fulfill the purposes described in this Policy, 
        comply with our legal obligations, resolve disputes, and enforce our agreements.
      </Paragraph>

      <Title level={3} className="subsection-title">10.1 Financial and Participation Records</Title>
      <Paragraph>
        <strong>Retention Period: 7 years from account closure</strong>
      </Paragraph>
      <Paragraph>
        When you close your account and exit the pool, we retain your financial and participation records for a period 
        of <strong>7 years</strong> from the date of account closure. This includes:
      </Paragraph>
      <ul className="terms-list">
        <li>Contribution records</li>
        <li>Profit allocation history</li>
        <li>Weekly settlement records</li>
        <li>Withdrawal requests</li>
        <li>Account balance information</li>
      </ul>
      <Paragraph>
        This retention period aligns with standard financial record-keeping requirements and ensures we can respond to 
        audits or regulatory inquiries, resolve disputes, and maintain accurate historical pool records.
      </Paragraph>

      <Title level={3} className="subsection-title">10.2 Audit and Operational Logs</Title>
      <Paragraph>
        <strong>Retention Period: Approximately 1 year</strong>
      </Paragraph>
      <Paragraph>
        System audit logs and operational records are retained for approximately <strong>1 year</strong> from the date 
        of creation. These logs include:
      </Paragraph>
      <ul className="terms-list">
        <li>User authentication events</li>
        <li>Administrative actions</li>
        <li>System operations</li>
        <li>Security events</li>
      </ul>
      <Paragraph>
        Audit logs older than 1 year are automatically deleted through a scheduled maintenance process.
      </Paragraph>

      <Title level={3} className="subsection-title">10.3 Closed Accounts</Title>
      <Paragraph>
        When you request to close your account, you can still log in to view your historical data, but you cannot create 
        new contributions or withdrawal requests. Your data is retained until the applicable retention period expires.
      </Paragraph>
      <Paragraph>
        <strong>Note:</strong> These retention periods are based on common financial industry practices and tax 
        record-keeping requirements. Actual retention periods may vary based on applicable federal and state laws, tax 
        regulations in your jurisdiction, regulatory requirements, and legal advice from counsel. This data retention 
        policy may be updated as we receive guidance from legal counsel and as regulations change.
      </Paragraph>

      <Title level={2} className="section-title">11. Your Rights and Choices</Title>
      <Paragraph>
        Depending on your location, you may have certain rights with respect to your personal information, such as:
      </Paragraph>
      <ul className="terms-list">
        <li><strong>Access.</strong> The right to request access to the personal information we hold about you.</li>
        <li><strong>Correction.</strong> The right to request that we correct inaccurate or incomplete personal information.</li>
        <li><strong>Deletion.</strong> The right to request deletion of your personal information, subject to legal 
        or contractual restrictions.</li>
        <li><strong>Restriction.</strong> The right to request restriction of processing in certain circumstances.</li>
        <li><strong>Objection.</strong> The right to object to certain types of processing, including processing based 
        on legitimate interests.</li>
        <li><strong>Data Portability.</strong> The right to request a copy of certain personal information in a 
        structured, commonly used, machine-readable format.</li>
      </ul>
      <Paragraph>
        You can exercise many of these rights by logging into your account and updating your information directly, 
        or by contacting us using the contact details in Section 13 below. We may need to verify your identity before 
        responding to certain requests.
      </Paragraph>
      <Paragraph>
        If you are located in the EEA, UK, or another jurisdiction that provides these rights, you also have the 
        right to lodge a complaint with your local data protection authority.
      </Paragraph>

      <Title level={2} className="section-title">12. Children's Privacy</Title>
      <Paragraph>
        The Service is not intended for use by children under the age of 18, and we do not knowingly collect personal 
        information from children under 18. If we become aware that we have collected personal information from a child 
        under 18, we will take reasonable steps to delete such information. If you believe a child under 18 has provided 
        us with personal information, please contact us.
      </Paragraph>

      <Title level={2} className="section-title">13. Changes to This Privacy Policy</Title>
      <Paragraph>
        We may update this Policy from time to time. When we do, we will revise the "Last updated" date at the top 
        of this document and may provide additional notice (such as via email or in-app notification) where required 
        by law.
      </Paragraph>
      <Paragraph>
        Your continued use of the Service after an updated Policy becomes effective indicates your acceptance of the 
        updated Policy. If you do not agree to the updated Policy, you must stop using the Service.
      </Paragraph>

      <Title level={2} className="section-title">14. Contact Us</Title>
      <Paragraph>
        If you have any questions or concerns about this Privacy Policy or our data practices, you may contact us at:
      </Paragraph>
      <Paragraph>
        <strong>DivvyStash</strong><br />
        Email: support@divvystash.com<br />
        Address: 2320 Prosperity Way, Suwanee, GA 30024
      </Paragraph>
    </div>
  );
};
